import { useState } from 'react';
import { ArrowLeft, Clock, Calendar as CalendarIcon, Plus, X, Check, Save } from 'lucide-react';

export default function AvailabilitySettings() {
  // State for availability settings
  const [availability, setAvailability] = useState({
    general: {
      days: ['Monday', 'Wednesday', 'Friday'],
      hours: {
        start: '09:00',
        end: '17:00'
      },
      timezone: 'WAT (West Africa Time)'
    },
    exceptions: [
      {
        id: 1,
        date: '2023-12-25',
        reason: 'Christmas Holiday',
        available: false
      },
      {
        id: 2,
        date: '2023-11-15',
        reason: 'Available for morning only',
        hours: {
          start: '09:00',
          end: '12:00'
        }
      }
    ]
  });

  // State for adding new exception
  const [newException, setNewException] = useState({
    date: '',
    reason: '',
    available: true,
    hours: {
      start: '09:00',
      end: '17:00'
    }
  });
  const [showAddException, setShowAddException] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  // Days of the week
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Toggle day availability
  const toggleDay = (day) => {
    const updatedDays = availability.general.days.includes(day)
      ? availability.general.days.filter(d => d !== day)
      : [...availability.general.days, day];
    
    setAvailability({
      ...availability,
      general: {
        ...availability.general,
        days: updatedDays
      }
    });
  };

  // Handle time change
  const handleTimeChange = (field, value) => {
    setAvailability({
      ...availability,
      general: {
        ...availability.general,
        hours: {
          ...availability.general.hours,
          [field]: value
        }
      }
    });
  };

  // Handle adding exception
  const handleAddException = () => {
    if (newException.date && newException.reason) {
      setAvailability({
        ...availability,
        exceptions: [...availability.exceptions, {
          ...newException,
          id: availability.exceptions.length + 1
        }]
      });
      setNewException({
        date: '',
        reason: '',
        available: true,
        hours: {
          start: '09:00',
          end: '17:00'
        }
      });
      setShowAddException(false);
    }
  };

  // Handle removing exception
  const handleRemoveException = (id) => {
    setAvailability({
      ...availability,
      exceptions: availability.exceptions.filter(exp => exp.id !== id)
    });
  };

  // API service function
  const updateExpertDetails = async (data) => {
    try {
      const response = await fetch('/expert/update-details', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update expert details');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating expert details:', error);
      throw error;
    }
  };

  const saveAvailabilityChanges = async () => {
    setIsLoading(true);
    try {
      const updateData = {
        available_days: availability.general.days,
        available_time: `${availability.general.hours.start}-${availability.general.hours.end}`
      };

      const result = await updateExpertDetails(updateData);
      
      if (result.success) {
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
      }
    } catch (error) {
      console.error('Error saving availability changes:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="account-settings-container-apss">
      <div className="account-settings-header-apss">
        <button className="back-button-apss" onClick={() => window.history.back()}>
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>
        <h1 className="account-settings-title-apss">Availability Settings</h1>
      </div>

      {/* General Availability Section */}
      <div className="settings-section-apss">
        <h3 className="section-title-apss">General Availability</h3>
        
        <div className="section-content-apss">
          {/* Days Selection */}
          <div className="availability-field-apss">
            <div className="field-label-apss">Available Days</div>
            <div className="days-selection-apss">
              {daysOfWeek.map(day => (
                <button
                  key={day}
                  className={`day-button-apss ${availability.general.days.includes(day) ? 'selected' : ''}`}
                  onClick={() => toggleDay(day)}
                >
                  {day.substring(0, 3)}
                </button>
              ))}
            </div>
          </div>
          
          {/* Hours Selection */}
          <div className="availability-field-apss">
            <div className="field-label-apss">Working Hours</div>
            <div className="hours-selection-apss">
              <div className="time-input-container-apss">
                <Clock size={16} className="time-icon-apss" />
                <input
                  type="time"
                  value={availability.general.hours.start}
                  onChange={(e) => handleTimeChange('start', e.target.value)}
                  className="time-input-apss"
                />
                <span className="time-separator-apss">to</span>
                <input
                  type="time"
                  value={availability.general.hours.end}
                  onChange={(e) => handleTimeChange('end', e.target.value)}
                  className="time-input-apss"
                />
              </div>
            </div>
          </div>
          
          {/* Timezone */}
          <div className="availability-field-apss">
            <div className="field-label-apss">Timezone</div>
            <div className="timezone-display-apss">
              {availability.general.timezone}
            </div>
          </div>
        </div>
      </div>

      {/* Exceptions Section */}
      <div className="settings-section-apss">
        <div className="exceptions-header-apss">
          <h3 className="section-title-apss">Special Dates & Exceptions</h3>
          {!showAddException && (
            <button 
              className="add-exception-button-apss"
              onClick={() => setShowAddException(true)}
            >
              <Plus size={16} />
              <span>Add Exception</span>
            </button>
          )}
        </div>
        
        {showAddException && (
          <div className="add-exception-container-apss">
            <div className="exception-form-apss">
              <div className="exception-field-apss">
                <label>Date</label>
                <input
                  type="date"
                  value={newException.date}
                  onChange={(e) => setNewException({...newException, date: e.target.value})}
                  className="exception-input-apss"
                />
              </div>
              
              <div className="exception-field-apss">
                <label>Reason</label>
                <input
                  type="text"
                  value={newException.reason}
                  onChange={(e) => setNewException({...newException, reason: e.target.value})}
                  placeholder="E.g. Conference, Holiday, etc."
                  className="exception-input-apss"
                />
              </div>
              
              <div className="exception-field-apss">
                <label>
                  <input
                    type="checkbox"
                    checked={newException.available}
                    onChange={(e) => setNewException({...newException, available: e.target.checked})}
                  />
                  <span>Available on this date</span>
                </label>
              </div>
              
              {newException.available && (
                <div className="exception-field-apss">
                  <label>Custom Hours (optional)</label>
                  <div className="exception-hours-apss">
                    <input
                      type="time"
                      value={newException.hours.start}
                      onChange={(e) => setNewException({
                        ...newException,
                        hours: {...newException.hours, start: e.target.value}
                      })}
                      className="exception-time-input-apss"
                    />
                    <span className="time-separator-apss">to</span>
                    <input
                      type="time"
                      value={newException.hours.end}
                      onChange={(e) => setNewException({
                        ...newException,
                        hours: {...newException.hours, end: e.target.value}
                      })}
                      className="exception-time-input-apss"
                    />
                  </div>
                </div>
              )}
              
              <div className="exception-actions-apss">
                <button 
                  className="save-exception-button-apss"
                  onClick={handleAddException}
                >
                  <Check size={16} />
                  <span>Save Exception</span>
                </button>
                <button 
                  className="cancel-exception-button-apss"
                  onClick={() => setShowAddException(false)}
                >
                  <X size={16} />
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="exceptions-list-apss">
          {availability.exceptions.length === 0 ? (
            <div className="no-exceptions-apss">
              No special dates or exceptions added yet
            </div>
          ) : (
            availability.exceptions.map(exception => (
              <div key={exception.id} className="exception-item-apss">
                <div className="exception-date-apss">
                  <CalendarIcon size={16} />
                  <span>{new Date(exception.date).toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  })}</span>
                </div>
                
                <div className="exception-details-apss">
                  <div className="exception-reason-apss">{exception.reason}</div>
                  {exception.available ? (
                    <div className="exception-hours-apss">
                      {exception.hours ? (
                        <>
                          {exception.hours.start} - {exception.hours.end}
                          <span className="exception-note-apss">(Custom hours)</span>
                        </>
                      ) : (
                        "Available all day"
                      )}
                    </div>
                  ) : (
                    <div className="exception-unavailable-apss">Not available</div>
                  )}
                </div>
                
                <button 
                  className="remove-exception-button-apss"
                  onClick={() => handleRemoveException(exception.id)}
                >
                  <X size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Save Changes Button */}
      <div className="save-changes-container-apss">
        <button 
          className="save-all-changes-button-apss"
          onClick={saveAvailabilityChanges}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Availability Changes'}
        </button>
      </div>

      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="success-alert-right-apss">
          <div className="success-alert-content-apss">
            <div className="success-alert-icon-apss">✓</div>
            <div className="success-alert-message-apss">
              Availability details updated successfully!
            </div>
          </div>
        </div>
      )}
    </div>
  );
}